// The price sticker for a phone we sell, printed by itself when the phone goes into inventory
// with its price ("Add to inventory"), or from a SICKW check in Phone check ("Use SICKW info &
// print price sticker"): a 3 x 2 in label on the store's Rollo (or a third bigger on a 4 x 3 in
// label, sideways across it, when Settings → Printers says the Rollo has those), stuck sideways on
// the back of the phone. The West Coast Wireless logo, the price big, the model and storage, the
// color, the carrier and whether it's locked, battery health, and the IMEI with its barcode
// (scanned at the counter, it rings the phone up).
import { Barcode, type PrintFn } from '../ui/print';
import type { CSSProperties } from 'react';
import { carrierLock, priceStickerShiftMm, priceStickerSize, stickerModel, stickerPrice, type PriceStickerSize } from '../../shared/receipts';
import { CARRIERS } from '../../shared/format';
import { useSession } from '../lib/session';
import { cloudPrinter } from '../lib/printing';
import { Field, Money, MoneyInput } from '../ui/kit';
import { get, qs } from '../lib/api';

export interface StickerPhone {
  brand: string;
  model: string;
  storage: string | null;
  color: string | null;
  carrier: string | null;
  locked: boolean;
  imei: string | null;
  serial: string | null;
  battery_health: number | null;
  price_cents: number;
  /** False when nobody knows whether it's locked (a sticker from a SICKW check that didn't say): the carrier alone shows. */
  lock_known?: boolean;
  /**
   * A wholesale sticker (the owner's): what the phone cost the store. It prints "Cost … · Sold …"
   * in place of the battery line, with the price on top being what the wholesale buyer paid.
   */
  cost_cents?: number | null;
}

/** "T-Mobile · Unlocked", or just the carrier when it isn't known whether it's locked. */
function lockLine(d: StickerPhone): string {
  if (d.lock_known !== false) return carrierLock(d);
  return d.carrier && !/^unlocked$/i.test(d.carrier) ? d.carrier : '';
}

/** The logo on the sticker: the West Coast Wireless name (not the receipt logo's address part), or the store's own logo. */
function stickerLogo(logo: string | null | undefined): string {
  if (!logo) return '';
  return /\/wcw-receipt-logo\.png$/.test(logo) ? '/assets/wcw-wordmark.png' : logo;
}

/** `shiftMm` (printing only): how far right everything prints (Settings → Printers → Move it right); the preview stays in the middle. */
export function PriceStickerPaper({ d, business, logo, size = '3x2', shiftMm = 0 }: { d: StickerPhone; business: string; logo?: string | null; size?: PriceStickerSize; shiftMm?: number }) {
  const img = stickerLogo(logo);
  const code = d.imei ?? d.serial;
  const price = stickerPrice(d.price_cents);
  const model = stickerModel(d);
  const line = [d.color, lockLine(d)].filter(Boolean).join(' · ');
  // Long prices ("$1,049.99") and names ("Samsung Galaxy S24 Ultra 512GB") print smaller so they fit.
  const priceSize = price.length >= 9 ? ' xlong' : price.length >= 8 ? ' long' : '';
  return (
    <div className={`psticker${size === '4x3' ? ' s4x3' : size === '2x2' ? ' s2x2' : ''}`} aria-label={`Price sticker ${model}`} style={shiftMm > 0 ? ({ '--ps-shift': `${shiftMm}mm` } as CSSProperties) : undefined}>
      <div className="ps-top">
        {img ? <img className="ps-logo" src={img} alt={business} /> : <span className="ps-biz">{business}</span>}
        <span className={`ps-price${priceSize}`}>{price}</span>
      </div>
      <div className={`ps-model${model.length > 30 ? ' xlong' : model.length > 26 ? ' long' : ''}`}>{model}</div>
      <div className={`ps-ln${line.length > 31 ? ' xlong' : line.length > 26 ? ' long' : ''}`}>{line}</div>
      {d.cost_cents !== null && d.cost_cents !== undefined ? <div className="ps-ln ps-batt">Cost {stickerPrice(d.cost_cents)} · Sold {price}</div> : d.battery_health ? <div className="ps-ln ps-batt">Battery health {d.battery_health}%</div> : null}
      {code ? <div className="ps-imei">{d.imei ? 'IMEI' : 'S/N'} {code}</div> : null}
      {code ? <div className="ps-bar"><Barcode value={code} height={30} /></div> : null}
    </div>
  );
}

/**
 * Prints price stickers, one label each: on the store's price sticker printer through PrintNode
 * when one is picked in Settings → Printers (no print window, so an iPad doesn't open its print
 * sheet), otherwise from this browser's print window like before. The sticker prints exactly as
 * it was edited on the screen.
 */
export function printPriceStickers(print: PrintFn, phones: StickerPhone[], settings?: { business_name?: string | null; receipt_logo_url?: string | null; price_sticker_size?: string | null; price_sticker_shift_mm?: string | null } | null) {
  if (!phones.length) return;
  const business = settings?.business_name || 'West Coast Wireless';
  const size = priceStickerSize(settings?.price_sticker_size);
  // The store's printer prints a little to the left of the label: everything moves right (not on
  // the 2 x 3 in label, where the sticker is turned sideways).
  const shiftMm = size === '2x3' ? 0 : priceStickerShiftMm(settings?.price_sticker_shift_mm);
  // Each one is its own label (the print wraps every document in its own keyed piece).
  const sticker = (d: StickerPhone) => <PriceStickerPaper d={d} business={business} logo={settings?.receipt_logo_url} size={size} shiftMm={shiftMm} />;
  // On a 2 x 3 in label the sticker is turned sideways inside a label-sized box (one label a page).
  const browser = () => print(
    phones.map((d) => (size === '2x3' ? <div className="ps-2x3">{sticker(d)}</div> : sticker(d))),
    size === '4x3' ? 'sticker-4x3' : size === '2x3' ? 'sticker-2x3' : size === '2x2' ? 'sticker-2x2' : 'sticker-3x2',
  );
  const cloud = cloudPrinter();
  if (!cloud) return browser();
  const first = phones[0];
  const code = first.imei ?? first.serial ?? stickerModel(first);
  const refId = `${code}${phones.length > 1 ? ` +${phones.length - 1}` : ''}`.slice(0, 80);
  const word = phones.every((d) => d.cost_cents !== null && d.cost_cents !== undefined) ? 'wholesale sticker' : 'price sticker';
  const title = phones.length > 1 ? `${phones.length} ${word}s` : `${word[0].toUpperCase()}${word.slice(1)} · ${stickerModel(first)}`;
  const data = {
    phones: phones.map((d) => ({
      brand: d.brand, model: d.model, storage: d.storage, color: d.color, carrier: d.carrier, locked: d.locked, lock_known: d.lock_known !== false,
      imei: d.imei, serial: d.serial, battery_health: d.battery_health, price_cents: d.price_cents, cost_cents: d.cost_cents ?? null,
    })),
  };
  cloud.printOut({ kind: 'price_sticker', refId, title, browser, data }).catch(() => browser());
}

// ---------------------------------------------------------------- editing a sticker before it prints

/** A price sticker being edited, word for word as it prints. lock "" = the sticker doesn't say. */
export interface StickerDraft {
  name: string;
  storage: string;
  color: string;
  /** "Unlocked", a carrier, or "" (not shown). */
  carrier: string;
  lock: 'locked' | 'unlocked' | '';
  price: number | null;
  /** Battery health % as typed. */
  battery: string;
}

/** The name as it prints ("iPhone 13 Pro", "Samsung Galaxy S22+"): no "Apple" in front of an iPhone. */
export function stickerName(brand: string, model: string): string {
  return brand && brand !== 'Apple' && !model.toLowerCase().startsWith(brand.toLowerCase()) ? `${brand} ${model}` : model;
}

/** Battery health is 1 to 100 (or empty). */
export const batteryProblem = (battery: string) => !!battery && (Number(battery) < 1 || Number(battery) > 100);

/** A phone in inventory, as its sticker prints today. */
export function draftFromPhone(d: StickerPhone): StickerDraft {
  const carrier = d.carrier?.trim() ?? '';
  return {
    name: stickerName(d.brand, d.model), storage: d.storage ?? '', color: d.color ?? '', carrier,
    lock: /^unlocked$/i.test(carrier) || !d.locked ? 'unlocked' : 'locked',
    price: d.price_cents > 0 ? d.price_cents : null, battery: d.battery_health ? String(d.battery_health) : '',
  };
}

/** What prints from a draft. */
export function stickerFromDraft(d: StickerDraft, ids: { imei: string | null; serial: string | null }): StickerPhone {
  const unlocked = d.carrier === 'Unlocked';
  return {
    brand: '', model: d.name.trim(), storage: d.storage.trim() || null, color: d.color.trim() || null,
    carrier: d.carrier || null, locked: !unlocked && d.lock === 'locked', lock_known: unlocked || d.lock !== '',
    imei: ids.imei, serial: ids.imei ? null : ids.serial,
    battery_health: d.battery && !batteryProblem(d.battery) ? Number(d.battery) : null, price_cents: d.price ?? 0,
  };
}

/**
 * The sticker's words (phone, storage, color, carrier, locked, price, battery) beside a preview
 * of the label (3 x 2 in, or 4 x 3 in sideways), exactly as it prints. Used from the inventory list, a phone's page and
 * Phone check (filled in from SICKW).
 */
export function StickerEditor({ draft, onChange, ids, carriers, priceDisabled, priceHint, batteryDisabled }: {
  draft: StickerDraft;
  onChange: (d: StickerDraft) => void;
  ids: { imei: string | null; serial: string | null };
  carriers?: readonly string[] | null;
  /** Shown, not changeable (no permission to change prices). */
  priceDisabled?: boolean;
  /** Under the price: where it came from ("the last one like it: $450"). */
  priceHint?: string;
  batteryDisabled?: boolean;
}) {
  const { me } = useSession();
  const set = (patch: Partial<StickerDraft>) => onChange({ ...draft, ...patch });
  const list = (carriers?.length ? carriers : CARRIERS).filter((c) => !/^(unlocked|other( carrier)?)$/i.test(c));
  const options = draft.carrier && draft.carrier !== 'Unlocked' && !list.includes(draft.carrier) ? [draft.carrier, ...list] : list;
  const unlocked = draft.carrier === 'Unlocked';
  const bad = batteryProblem(draft.battery);
  return (
    <div className="ps-edit">
      <div className="form-grid">
        <Field label="Phone" className="full"><input className="input" value={draft.name} onChange={(e) => set({ name: e.target.value })} maxLength={60} placeholder="e.g. iPhone 13 Pro" /></Field>
        <Field label="Storage"><input className="input" value={draft.storage} onChange={(e) => set({ storage: e.target.value })} maxLength={12} placeholder="128GB" /></Field>
        <Field label="Color"><input className="input" value={draft.color} onChange={(e) => set({ color: e.target.value })} maxLength={30} placeholder="e.g. Sierra Blue" /></Field>
        <Field label="Carrier">
          <select className="select" value={draft.carrier} onChange={(e) => set(e.target.value === 'Unlocked' ? { carrier: 'Unlocked', lock: 'unlocked' } : { carrier: e.target.value })}>
            <option value="">Don’t show</option>
            <option value="Unlocked">Unlocked</option>
            {options.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </Field>
        {unlocked ? <span /> : (
          <Field label="Locked?">
            <select className="select" value={draft.lock} onChange={(e) => set({ lock: e.target.value as StickerDraft['lock'] })}>
              <option value="locked">{draft.carrier ? `Locked to ${draft.carrier}` : 'Locked'}</option>
              <option value="unlocked">Unlocked</option>
              <option value="">Don’t show</option>
            </select>
          </Field>
        )}
        <Field label="Price" hint={priceDisabled ? 'a manager changes prices' : priceHint}>
          {priceDisabled ? <div className="input"><Money cents={draft.price ?? 0} /></div> : <MoneyInput value={draft.price} onChange={(v) => set({ price: v })} autoFocus={!draft.price} ariaLabel="Sticker price" />}
        </Field>
        <Field label="Battery health %" hint={bad ? '1 to 100' : 'optional: Settings → Battery'}>
          <input className="input" inputMode="numeric" value={draft.battery} disabled={batteryDisabled} onChange={(e) => set({ battery: e.target.value.replace(/\D/g, '').slice(0, 3) })} placeholder="e.g. 88" aria-invalid={bad} />
        </Field>
      </div>
      <div className="ps-edit-preview" aria-label="How the sticker prints">
        <PriceStickerPaper d={stickerFromDraft(draft, ids)} business={me?.settings?.business_name || 'West Coast Wireless'} logo={me?.settings?.receipt_logo_url} size={priceStickerSize(me?.settings?.price_sticker_size)} />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- the price the store asks for one like it

/** A phone in inventory or sold at the register, as /api/devices lists it: enough to price one like it. */
export interface PricedLike {
  id: string;
  title: string;
  brand: string;
  model: string;
  storage: string | null;
  carrier: string | null;
  locked: boolean;
  grade: string | null;
  status: string;
  price_cents: number;
  sold_sale_id: string | null;
  created_at: number;
  sold_at: number | null;
}

const specKey = (s: string | null | undefined) => (s ?? '').toLowerCase().replace(/[^a-z0-9]+/g, '');

/**
 * What the store last asked for a phone like this one, so the price is remembered: the same
 * brand, model and storage, from inventory and phones sold at the register (not wholesale, not
 * parts); one with the same carrier and lock first, then the newest. Null when there's none.
 */
export async function lastPriceLike(spec: { brand: string; model: string; storage?: string | null; carrier?: string | null; locked?: boolean }): Promise<PricedLike | null> {
  const model = spec.model.trim();
  if (!model) return null;
  const rows = await get<PricedLike[]>(`/api/devices${qs({ q: `${spec.brand.trim()} ${model}`.trim(), status: 'any', location: 'all', limit: 300 })}`).catch(() => [] as PricedLike[]);
  const like = rows.filter((d) =>
    d.price_cents > 0 && d.grade !== 'parts' && specKey(d.model) === specKey(model) && (!spec.brand.trim() || specKey(d.brand) === specKey(spec.brand))
    && specKey(d.storage) === specKey(spec.storage) && (d.status === 'in_stock' || d.status === 'on_hold' || (d.status === 'sold' && !!d.sold_sale_id)));
  const same = spec.carrier ? like.filter((d) => specKey(d.carrier) === specKey(spec.carrier) && d.locked === !!spec.locked) : [];
  const newest = (list: PricedLike[]) => [...list].sort((a, b) => (b.sold_at ?? b.created_at) - (a.sold_at ?? a.created_at))[0] ?? null;
  return newest(same) ?? newest(like);
}
